import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../apiBase';
import GuardianRelationshipSelect from './GuardianRelationshipSelect';
import { DEFAULT_GUARDIAN_RELATIONSHIP } from '../utils/guardianRelationships';

export default function LinkGuardianModal({ studentId, studentName, onClose, onLinked }) {
    const { t } = useTranslation(['guardianLink', 'common']);
    const [phone, setPhone] = useState('');
    const [relationship, setRelationship] = useState(DEFAULT_GUARDIAN_RELATIONSHIP);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [lookup, setLookup] = useState(null);

    const api = axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });

    const lookupTimer = useRef(null);

    useEffect(() => {
        clearTimeout(lookupTimer.current);
        const trimmed = phone.trim();
        if (trimmed.length < 10) {
            setLookup(null);
            return;
        }
        lookupTimer.current = setTimeout(async () => {
            try {
                const res = await api.get('/guardian/lookup-by-phone', { params: { phone: trimmed } });
                setLookup(res.data);
            } catch {
                setLookup(null);
            }
        }, 400);
        return () => clearTimeout(lookupTimer.current);
    }, [phone]);

    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            await api.post(`/guardian/students/${studentId}/links`, {
                phone: phone.trim(),
                relationship
            });
            onLinked?.();
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || t('guardianLink:linkError'));
        }
        setBusy(false);
    }

    const selectStyle = {
        width: '100%', padding: '12px', borderRadius: '10px',
        border: '1px solid var(--sp-gray-200)', boxSizing: 'border-box', marginBottom: '12px',
        fontSize: '14px'
    };

    return (
        <div
            className="modal-overlay"
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
            }}
            onClick={onClose}
        >
            <div
                className="modal"
                style={{
                    background: '#fff', borderRadius: '16px', padding: '24px',
                    maxWidth: '420px', width: '92%'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="drag-handle" aria-hidden="true" />
                <h3 style={{ margin: '0 0 8px' }}>{t('guardianLink:title', { name: studentName })}</h3>
                <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#64748b', lineHeight: 1.5 }}>
                    {t('guardianLink:intro')}
                </p>
                <form onSubmit={submit}>
                    <label style={lbl}>{t('guardianLink:relationship')}</label>
                    <GuardianRelationshipSelect
                        value={relationship}
                        onChange={setRelationship}
                        style={selectStyle}
                        required
                    />
                    <label style={lbl}>{t('guardianLink:phoneLabel')}</label>
                    <input
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+7..."
                        required
                        style={{
                            width: '100%', padding: '12px', borderRadius: '10px',
                            border: '1px solid var(--sp-gray-200)', boxSizing: 'border-box', marginBottom: '8px'
                        }}
                    />
                    {lookup?.found && (
                        <p style={{ fontSize: '13px', color: '#059669', margin: '0 0 12px' }}>
                            {t('guardianLink:found', {
                                name: lookup.displayName || t('guardianLink:foundFallback')
                            })}
                        </p>
                    )}
                    {lookup && !lookup.found && phone.trim().length >= 10 && (
                        <p style={{ fontSize: '13px', color: '#b45309', margin: '0 0 12px' }}>
                            {t('guardianLink:notFound')}
                        </p>
                    )}
                    {error && <p style={{ color: '#c62828', fontSize: '13px' }}>{error}</p>}
                    <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                        <button type="button" onClick={onClose} style={btnSec}>{t('common:cancel')}</button>
                        <button type="submit" disabled={busy} style={btnPri}>
                            {busy ? '...' : t('guardianLink:submit')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

const lbl = { display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px' };
const btnPri = {
    flex: 1, padding: '11px', border: 'none', borderRadius: '10px',
    background: '#EB3333', color: '#fff', fontWeight: 700, cursor: 'pointer'
};
const btnSec = {
    flex: 1, padding: '11px', border: '1px solid var(--sp-gray-200)', borderRadius: '10px',
    background: '#fff', cursor: 'pointer'
};
